import React, { useMemo, useState } from "react";
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  TextField,
  InputAdornment,
  Chip,
} from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";
import { Search } from "@mui/icons-material";
import {
  ManagerStatisticsData,
  ManagerStatisticsImage,
} from "../../../types/statistics";
import {
  imageStyles,
  sectionTitleStyles,
  cardStyles,
  cardContentStyles,
  tableContainerStyles,
} from "../styles";
import { TeamStatisticsFocusFilter } from "../team-statistics-focus-filter";
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ReferenceLine,
} from "recharts";

type ParsedTable = { columns: string[]; rows: string[][] };

const normalize = (s: string) => s.trim().toLowerCase().replace(/\s+/g, " ");

const parseFirstHtmlTable = (html: string): ParsedTable | null => {
  try {
    const doc = new DOMParser().parseFromString(html, "text/html");
    const table = doc.querySelector("table");
    if (!table) return null;

    const headerCells = Array.from(table.querySelectorAll("thead th"));
    const columnsFromTh = headerCells
      .map((th) => th.textContent?.trim() || "")
      .filter(Boolean);

    const bodyRows = Array.from(table.querySelectorAll("tbody tr"));
    const fallbackRows =
      bodyRows.length > 0
        ? bodyRows
        : Array.from(table.querySelectorAll("tr")).slice(1);

    const rows = fallbackRows.map((tr) =>
      Array.from(tr.querySelectorAll("td, th")).map(
        (cell) => cell.textContent?.trim() || ""
      )
    );

    const columns =
      columnsFromTh.length > 0
        ? columnsFromTh
        : Array.from(
            { length: Math.max(0, rows[0]?.length ?? 0) },
            (_, i) => `Column ${i + 1}`
          );

    const normalizedRows = rows.map((r) => {
      if (r.length === columns.length) return r;
      const padded = [...r];
      while (padded.length < columns.length) padded.push("");
      return padded.slice(0, columns.length);
    });

    return { columns, rows: normalizedRows };
  } catch {
    return null;
  }
};

const findColIdx = (columns: string[], includesAll: string[]) => {
  const normCols = columns.map(normalize);
  return normCols.findIndex((c) =>
    includesAll.every((k) => c.includes(normalize(k)))
  );
};

const parseNumber = (raw: string): number | null => {
  const s = (raw || "").replace(",", ".").trim();
  const m = s.match(/-?\d+(\.\d+)?/);
  if (!m) return null;
  const n = Number(m[0]);
  return Number.isFinite(n) ? n : null;
};

interface ChartsSectionProps {
  images: ManagerStatisticsImage[];
  charts?: ManagerStatisticsData["charts"];
  tableHtmls?: string[];
}

export const ChartsSection: React.FC<ChartsSectionProps> = ({
  images,
  charts,
  tableHtmls,
}) => {
  const safeImages = useMemo(() => images ?? [], [images]);
  const theme = useTheme();

  const MONTHLY_CHART_HEIGHT = 320;
  const C4_CHART_HEIGHT = 360;

  const [searchTerm, setSearchTerm] = useState<string>("");
  const [focusValue, setFocusValue] = useState<string>("");

  const monthly = charts?.monthlyOverallProgress;
  const c4 = charts?.c4Progress;
  const hasRawCharts =
    Boolean(monthly?.data?.length) || Boolean(c4?.data?.length);

  type RowMetric = {
    name: string;
    focus: string;
    currentReadiness: number | null;
    pastReadiness7d: number | null;
    preAssessmentReadiness: number | null;
  };

  const derived = useMemo(() => {
    const htmls = (tableHtmls || []).filter(Boolean);
    if (htmls.length === 0) return null;

    const allRows: RowMetric[] = [];

    for (const html of htmls) {
      const parsed = parseFirstHtmlTable(html);
      if (!parsed) continue;

      const { columns, rows } = parsed;
      const nameIdx =
        findColIdx(columns, ["name"]) !== -1
          ? findColIdx(columns, ["name"])
          : 0;

      const focusIdx = (() => {
        const idx = findColIdx(columns, ["focus"]);
        return idx;
      })();

      const currentReadinessIdx = (() => {
        const idx = findColIdx(columns, ["current", "overall", "readiness"]);
        return idx;
      })();

      const pastReadinessIdx = (() => {
        const idx = findColIdx(columns, ["past", "overall", "readiness"]);
        return idx;
      })();

      const preAssessmentIdx = (() => {
        const idx = findColIdx(columns, [
          "pre-assessment",
          "overall",
          "readiness",
        ]);
        return idx;
      })();

      rows.forEach((r) => {
        const name = (r[nameIdx] ?? "").trim();
        if (!name) return;
        const focus = focusIdx >= 0 ? (r[focusIdx] ?? "").trim() : "";
        const currentReadiness =
          currentReadinessIdx >= 0 ? parseNumber(r[currentReadinessIdx]) : null;
        const pastReadiness7d =
          pastReadinessIdx >= 0 ? parseNumber(r[pastReadinessIdx]) : null;
        const preAssessmentReadiness =
          preAssessmentIdx >= 0 ? parseNumber(r[preAssessmentIdx]) : null;

        allRows.push({
          name,
          focus,
          currentReadiness,
          pastReadiness7d,
          preAssessmentReadiness,
        });
      });
    }

    if (allRows.length === 0) return null;

    const focusOptions = Array.from(
      new Set(allRows.map((r) => r.focus).filter(Boolean))
    ).sort((a, b) => a.localeCompare(b));

    return { allRows, focusOptions };
  }, [tableHtmls]);

  const filteredDerivedRows = useMemo(() => {
    if (!derived) return [];
    let rows = derived.allRows;

    if (focusValue) {
      const sel = focusValue.toLowerCase();
      rows = rows.filter((r) => (r.focus || "").toLowerCase() === sel);
    }

    if (searchTerm) {
      const q = searchTerm.toLowerCase();
      rows = rows.filter(
        (r) =>
          r.name.toLowerCase().includes(q) ||
          (r.focus || "").toLowerCase().includes(q)
      );
    }

    return rows;
  }, [derived, focusValue, searchTerm]);

  const hasDerivedCharts = filteredDerivedRows.length > 0;

  const derivedProgressOverTime = useMemo(() => {
    if (!hasDerivedCharts) return [];

    const avg = (
      key: "preAssessmentReadiness" | "pastReadiness7d" | "currentReadiness"
    ) => {
      let sum = 0;
      let cnt = 0;
      filteredDerivedRows.forEach((r) => {
        const v = r[key];
        if (typeof v === "number" && Number.isFinite(v)) {
          sum += v;
          cnt += 1;
        }
      });
      return cnt ? sum / cnt : null;
    };

    const points: Array<{ label: string; progress: number }> = [];
    const pre = avg("preAssessmentReadiness");
    const past = avg("pastReadiness7d");
    const current = avg("currentReadiness");

    if (pre != null) points.push({ label: "Pre", progress: pre });
    if (past != null) points.push({ label: "7d ago", progress: past });
    if (current != null) points.push({ label: "Current", progress: current });

    return points;
  }, [filteredDerivedRows, hasDerivedCharts]);

  const derivedMonthlyOverTimeFromDataTable = useMemo(() => {
    const html = (tableHtmls || [])[0];
    if (!html) return [];
    const parsed = parseFirstHtmlTable(html);
    if (!parsed) return [];

    const { columns, rows } = parsed;
    const nameIdx =
      findColIdx(columns, ["name"]) !== -1 ? findColIdx(columns, ["name"]) : 0;
    const focusIdx = (() => {
      const idx = findColIdx(columns, ["focus"]);
      return idx !== -1 ? idx : columns.length > 1 && nameIdx === 0 ? 1 : -1;
    })();

    const currentIdx = (() => {
      const idx = findColIdx(columns, ["current", "overall", "readiness"]);
      return idx;
    })();

    const pastIdx = (() => {
      const idx =
        findColIdx(columns, ["past", "overall", "readiness"]) !== -1
          ? findColIdx(columns, ["past", "overall", "readiness"])
          : findColIdx(columns, ["7", "days", "ago"]);
      return idx;
    })();

    const preIdx = (() => {
      const idx = findColIdx(columns, [
        "pre-assessment",
        "overall",
        "readiness",
      ]);
      return idx;
    })();

    const userRows = rows
      .map((r) => {
        const name = (r[nameIdx] ?? "").trim();
        if (!name) return null;
        const focus = focusIdx >= 0 ? (r[focusIdx] ?? "").trim() : "";
        const pre = preIdx >= 0 ? parseNumber(r[preIdx] ?? "") : null;
        const past = pastIdx >= 0 ? parseNumber(r[pastIdx] ?? "") : null;
        const current =
          currentIdx >= 0 ? parseNumber(r[currentIdx] ?? "") : null;
        return { name, focus, pre, past, current };
      })
      .filter(Boolean) as Array<{
      name: string;
      focus: string;
      pre: number | null;
      past: number | null;
      current: number | null;
    }>;

    let filtered = userRows;
    if (focusValue) {
      const sel = focusValue.toLowerCase();
      filtered = filtered.filter((r) => (r.focus || "").toLowerCase() === sel);
    }
    if (searchTerm) {
      const q = searchTerm.toLowerCase();
      filtered = filtered.filter(
        (r) =>
          (r.name || "").toLowerCase().includes(q) ||
          (r.focus || "").toLowerCase().includes(q)
      );
    }

    const avg = (vals: Array<number | null>) => {
      const nums = vals.filter(
        (v): v is number => typeof v === "number" && Number.isFinite(v)
      );
      if (nums.length === 0) return null;
      return nums.reduce((a, b) => a + b, 0) / nums.length;
    };

    const pre = avg(filtered.map((r) => r.pre));
    const past = avg(filtered.map((r) => r.past));
    const current = avg(filtered.map((r) => r.current));

    const points: Array<{ label: string; progress: number }> = [];
    if (pre != null) points.push({ label: "Pre", progress: pre });
    if (past != null) points.push({ label: "7d ago", progress: past });
    if (current != null) points.push({ label: "Current", progress: current });
    return points;
  }, [tableHtmls, focusValue, searchTerm]);

  const derivedC4ByUserFromDataTable = useMemo(() => {
    const html = (tableHtmls || [])[0];
    if (!html) return [];
    const parsed = parseFirstHtmlTable(html);
    if (!parsed) return [];

    const { columns, rows } = parsed;
    const nameIdx =
      findColIdx(columns, ["name"]) !== -1 ? findColIdx(columns, ["name"]) : 0;
    const focusIdx = (() => {
      const idx = findColIdx(columns, ["focus"]);
      return idx !== -1 ? idx : columns.length > 1 && nameIdx === 0 ? 1 : -1;
    })();

    const normCols = columns.map(normalize);
    const categories = ["consumers", "customers", "compounds", "competitors"];

    const baselineIdxs: number[] = [];
    const comparisonIdxs: number[] = [];

    for (const cat of categories) {
      const baseIdx = normCols.findIndex(
        (c) => c.includes("baseline") && c.includes(`c4-${cat}`)
      );
      if (baseIdx >= 0) baselineIdxs.push(baseIdx);

      const compIdx = normCols.findIndex(
        (c) => !c.includes("baseline") && c.includes(`c4-${cat}`)
      );
      if (compIdx >= 0) comparisonIdxs.push(compIdx);
    }

    const avg = (vals: Array<number | null>) => {
      const nums = vals.filter(
        (v): v is number => typeof v === "number" && Number.isFinite(v)
      );
      if (nums.length === 0) return 0;
      return nums.reduce((a, b) => a + b, 0) / nums.length;
    };

    let data = rows
      .map((r) => {
        const name = (r[nameIdx] ?? "").trim();
        if (!name) return null;
        const focus = focusIdx >= 0 ? (r[focusIdx] ?? "").trim() : "";
        const baseline = avg(baselineIdxs.map((i) => parseNumber(r[i] ?? "")));
        const comparison = avg(
          comparisonIdxs.map((i) => parseNumber(r[i] ?? ""))
        );
        const baselineRounded = Number(baseline.toFixed(2));
        const comparisonRounded = Number(comparison.toFixed(2));

        const label = focus ? `${name} (${focus})` : name;
        return {
          name,
          label,
          baseline: baselineRounded,
          comparison: comparisonRounded,
          _rawName: name,
          _focus: focus,
        };
      })
      .filter(Boolean) as Array<{
      name: string;
      label: string;
      baseline: number;
      comparison: number;
      _rawName: string;
      _focus: string;
    }>;

    if (focusValue) {
      const sel = focusValue.toLowerCase();
      data = data.filter((d) => (d._focus || "").toLowerCase() === sel);
    }
    if (searchTerm) {
      const q = searchTerm.toLowerCase();
      data = data.filter(
        (d) =>
          (d._rawName || "").toLowerCase().includes(q) ||
          (d._focus || "").toLowerCase().includes(q)
      );
    }

    data.sort((a, b) => b.comparison - a.comparison);
    return data;
  }, [tableHtmls, focusValue, searchTerm]);

  const derivedBaselineComparisonByUser = useMemo(() => {
    if (!hasDerivedCharts) return [];
    if (derivedC4ByUserFromDataTable.length > 0)
      return derivedC4ByUserFromDataTable as Array<{
        name: string;
        label?: string;
        baseline: number;
        comparison: number;
      }>;

    // Fallback: derive something C4-like from readiness when C4 columns are missing.
    const data = filteredDerivedRows
      .map((r) => {
        const baseline = r.pastReadiness7d ?? r.preAssessmentReadiness;
        const comparison = r.currentReadiness;
        if (baseline == null || comparison == null) return null;
        return {
          name: r.name,
          label: r.name,
          baseline: baseline * 20,
          comparison: comparison * 20,
        };
      })
      .filter(Boolean) as Array<{
      name: string;
      label?: string;
      baseline: number;
      comparison: number;
    }>;

    data.sort((a, b) => b.comparison - a.comparison);
    return data;
  }, [filteredDerivedRows, hasDerivedCharts, derivedC4ByUserFromDataTable]);

  const derivedC4MinWidth = useMemo(() => {
    const n = derivedBaselineComparisonByUser.length;
    if (n <= 10) return undefined;
    return Math.max(1100, n * 120);
  }, [derivedBaselineComparisonByUser.length]);

  const derivedMonthlyMinWidth = useMemo(() => {
    const n =
      derivedMonthlyOverTimeFromDataTable.length ||
      derivedProgressOverTime.length;
    if (n <= 3) return undefined;
    return Math.max(600, n * 200);
  }, [
    derivedMonthlyOverTimeFromDataTable.length,
    derivedProgressOverTime.length,
  ]);

  const filteredImages = useMemo(() => {
    if (!searchTerm) return safeImages;
    const q = searchTerm.toLowerCase();
    return safeImages.filter((img) =>
      (img.name || "").toLowerCase().includes(q)
    );
  }, [safeImages, searchTerm]);

  const dataUsersCount = useMemo(() => {
    const html = (tableHtmls || [])[0];
    if (!html) return 0;
    const parsed = parseFirstHtmlTable(html);
    if (!parsed) return 0;

    const { columns, rows } = parsed;
    const nameIdx =
      findColIdx(columns, ["name"]) !== -1 ? findColIdx(columns, ["name"]) : 0;
    const focusIdx = (() => {
      const idx = findColIdx(columns, ["focus"]);
      return idx !== -1 ? idx : columns.length > 1 && nameIdx === 0 ? 1 : -1;
    })();

    let count = 0;
    for (const r of rows) {
      const name = (r[nameIdx] ?? "").trim();
      if (!name) continue;
      const focus = focusIdx >= 0 ? (r[focusIdx] ?? "").trim() : "";

      if (focusValue) {
        if ((focus || "").toLowerCase() !== focusValue.toLowerCase()) continue;
      }
      if (searchTerm) {
        const q = searchTerm.toLowerCase();
        if (!name.toLowerCase().includes(q) && !focus.toLowerCase().includes(q))
          continue;
      }

      count += 1;
    }
    return count;
  }, [tableHtmls, focusValue, searchTerm]);

  const tooltipContentStyle = useMemo(
    () => ({
      backgroundColor: theme.palette.background.paper,
      border: `1px solid ${theme.palette.divider}`,
      color: theme.palette.text.primary,
      borderRadius: 8,
      boxShadow:
        theme.palette.mode === "dark"
          ? "0 8px 24px rgba(0,0,0,0.45)"
          : "0 8px 24px rgba(0,0,0,0.18)",
    }),
    [theme.palette]
  );

  const tooltipLabelStyle = useMemo(
    () => ({
      color: theme.palette.text.primary,
      fontWeight: 600,
    }),
    [theme.palette.text.primary]
  );

  const tooltipItemStyle = useMemo(
    () => ({
      color: theme.palette.text.primary,
    }),
    [theme.palette.text.primary]
  );

  const percentTooltipFormatter = useMemo(
    () => (value: any) => {
      const n = typeof value === "number" ? value : Number(value);
      return Number.isFinite(n) ? `${n.toFixed(2)}%` : value;
    },
    []
  );

  const chartTheme = useMemo(() => {
    const isDark = theme.palette.mode === "dark";
    return {
      isDark,
      axisTickFill: theme.palette.text.secondary,
      axisLineStroke: theme.palette.divider,
      gridStroke: isDark
        ? alpha(theme.palette.common.white, 0.18)
        : alpha(theme.palette.common.black, 0.08),
      legendText: theme.palette.text.secondary,
      progressFill: isDark ? "#818cf8" : "#4f46e5",
      baselineFill: isDark ? "#94a3b8" : "#9ca3af",
      comparisonFill: isDark ? "#fb7185" : "#ef4444",
      monthlyBg: isDark ? alpha(theme.palette.info.main, 0.18) : "#e0f2fe",
      c4Bg: isDark ? alpha("#66EF64", 0.18) : "#66EF64B2",
      gridOnLightBg: alpha(theme.palette.common.white, 0.9),
    };
  }, [theme.palette]);

  const legendFormatter = useMemo(
    () => (value: any) => (
      <span style={{ color: chartTheme.legendText }}>{String(value)}</span>
    ),
    [chartTheme.legendText]
  );

  if (!hasRawCharts && !hasDerivedCharts && safeImages.length === 0)
    return null;

  const showImagesFallback = !hasRawCharts && !hasDerivedCharts;

  return (
    <Box sx={{ mb: 3 }}>
      <Typography variant="h6" sx={sectionTitleStyles}>
        Charts & Graphs
      </Typography>
      <Box
        sx={{
          display: "flex",
          alignItems: "flex-end",
          gap: 2,
          flexWrap: "wrap",
          mb: 2,
        }}
      >
        <TextField
          placeholder={
            hasDerivedCharts ? "Search users/focus..." : "Search charts..."
          }
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          size="small"
          sx={{ maxWidth: 420, flexGrow: 1 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search />
              </InputAdornment>
            ),
          }}
        />
        {derived?.focusOptions?.length ? (
          <TeamStatisticsFocusFilter
            label="Focus"
            value={focusValue}
            onChange={setFocusValue}
            options={derived.focusOptions}
          />
        ) : null}
        <Chip
          label={
            hasDerivedCharts
              ? `Users: ${dataUsersCount}`
              : `Found: ${filteredImages.length} of ${safeImages.length}`
          }
          color={
            hasDerivedCharts
              ? focusValue || searchTerm
                ? "success"
                : "default"
              : filteredImages.length < safeImages.length
                ? "success"
                : "default"
          }
          variant="outlined"
          size="small"
        />
      </Box>
      <Grid container spacing={2}>
        {/* Raw charts (preferred) */}
        {monthly?.data?.length ? (
          <Grid item xs={12} md={6}>
            <Card sx={cardStyles}>
              <CardContent sx={cardContentStyles}>
                <Typography variant="subtitle1" sx={{ mb: 1 }}>
                  {monthly.title || "Monthly Overall Progress"}
                </Typography>
                <Box sx={{ width: "100%", height: MONTHLY_CHART_HEIGHT }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={monthly.data}>
                      <CartesianGrid
                        strokeDasharray="3 3"
                        stroke={chartTheme.gridStroke}
                      />
                      <XAxis
                        dataKey="date"
                        tick={{ fontSize: 12, fill: chartTheme.axisTickFill }}
                        axisLine={{ stroke: chartTheme.axisLineStroke }}
                        tickLine={{ stroke: chartTheme.axisLineStroke }}
                      />
                      <YAxis
                        domain={monthly.yDomain ?? [0, 5]}
                        ticks={[0, 1, 2, 3, 4, 5]}
                        tick={{ fontSize: 12, fill: chartTheme.axisTickFill }}
                        axisLine={{ stroke: chartTheme.axisLineStroke }}
                        tickLine={{ stroke: chartTheme.axisLineStroke }}
                        label={{
                          value: "Progress",
                          angle: -90,
                          position: "inside",
                          style: { textAnchor: "middle", dx: -15 },
                        }}
                      />
                      <Tooltip
                        contentStyle={tooltipContentStyle}
                        labelStyle={tooltipLabelStyle}
                        itemStyle={tooltipItemStyle}
                        formatter={percentTooltipFormatter}
                      />
                      <Legend
                        wrapperStyle={{ color: chartTheme.legendText }}
                        formatter={legendFormatter}
                      />
                      <ReferenceLine
                        y={5}
                        stroke="#66EF64"
                        strokeDasharray="6 4"
                        strokeOpacity={0.7}
                      />
                      {typeof monthly.readinessThreshold === "number" && (
                        <ReferenceLine
                          y={monthly.readinessThreshold}
                          stroke="#f59e0b"
                          strokeDasharray="6 4"
                          label="Readiness Threshold"
                        />
                      )}
                      <Bar
                        dataKey="progress"
                        name="Progress"
                        fill={chartTheme.progressFill}
                        radius={[6, 6, 0, 0]}
                      />
                    </BarChart>
                  </ResponsiveContainer>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        ) : null}

        {c4?.data?.length ? (
          <Grid item xs={12} md={6}>
            <Card sx={cardStyles}>
              <CardContent sx={cardContentStyles}>
                <Typography variant="subtitle1" sx={{ mb: 1 }}>
                  {c4.title || "C4 Progress Chart"}
                </Typography>
                <Box
                  sx={{
                    width: "100%",
                    height: C4_CHART_HEIGHT,
                    backgroundColor: chartTheme.c4Bg,
                    borderRadius: "8px",
                    p: 1,
                  }}
                >
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={c4.data}>
                      <CartesianGrid
                        strokeDasharray="3 3"
                        stroke={chartTheme.gridOnLightBg}
                      />
                      <XAxis
                        dataKey="category"
                        tick={{ fontSize: 12, fill: chartTheme.axisTickFill }}
                        axisLine={{ stroke: chartTheme.axisLineStroke }}
                        tickLine={{ stroke: chartTheme.axisLineStroke }}
                      />
                      <YAxis
                        domain={[0, 100]}
                        ticks={[0, 20, 40, 60, 80, 100]}
                        tick={{ fontSize: 12, fill: chartTheme.axisTickFill }}
                        axisLine={{ stroke: chartTheme.axisLineStroke }}
                        tickLine={{ stroke: chartTheme.axisLineStroke }}
                        label={{
                          value: "Percentage (%)",
                          angle: -90,
                          position: "inside",
                          style: { textAnchor: "middle" },
                          dx: -10,
                        }}
                      />
                      <Tooltip
                        contentStyle={tooltipContentStyle}
                        labelStyle={tooltipLabelStyle}
                        itemStyle={tooltipItemStyle}
                        formatter={percentTooltipFormatter}
                      />
                      <Legend
                        wrapperStyle={{ color: chartTheme.legendText }}
                        formatter={legendFormatter}
                      />
                      <Bar
                        dataKey="baseline"
                        name="Baseline"
                        fill={chartTheme.baselineFill}
                        radius={[6, 6, 0, 0]}
                      />
                      <Bar
                        dataKey="comparison"
                        name="Comparison"
                        fill={chartTheme.comparisonFill}
                        radius={[6, 6, 0, 0]}
                      />
                    </BarChart>
                  </ResponsiveContainer>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        ) : null}

        {/* Derived charts from existing tables (frontend-only) */}
        {!hasRawCharts && hasDerivedCharts ? (
          <>
            <Grid item xs={12} md={6}>
              <Card sx={cardStyles}>
                <CardContent sx={cardContentStyles}>
                  <Typography
                    variant="subtitle1"
                    sx={{ mb: 1, textAlign: "center" }}
                  >
                    Monthly Overall Progress
                  </Typography>
                  <Box
                    sx={{
                      width: "100%",
                      overflowX: derivedMonthlyMinWidth ? "auto" : "hidden",
                      overflowY: "hidden",
                      ...tableContainerStyles,
                    }}
                  >
                    <Box
                      sx={{
                        minWidth: derivedMonthlyMinWidth ?? "100%",
                        height: MONTHLY_CHART_HEIGHT,
                        pr: 2,
                        backgroundColor: chartTheme.monthlyBg,
                        borderRadius: "8px",
                        p: 1,
                      }}
                    >
                      <ResponsiveContainer width="100%" height="100%">
                        <BarChart
                          data={
                            derivedMonthlyOverTimeFromDataTable.length > 0
                              ? derivedMonthlyOverTimeFromDataTable
                              : derivedProgressOverTime
                          }
                        >
                          <CartesianGrid
                            strokeDasharray="3 3"
                            stroke={
                              chartTheme.isDark
                                ? chartTheme.gridStroke
                                : chartTheme.gridOnLightBg
                            }
                          />
                          <XAxis
                            dataKey="label"
                            tick={{
                              fontSize: 12,
                              fill: chartTheme.axisTickFill,
                            }}
                            axisLine={{ stroke: chartTheme.axisLineStroke }}
                            tickLine={{ stroke: chartTheme.axisLineStroke }}
                          />
                          <YAxis
                            domain={[0, 5]}
                            ticks={[0, 1, 2, 3, 4, 5]}
                            tick={{
                              fontSize: 12,
                              fill: chartTheme.axisTickFill,
                            }}
                            axisLine={{ stroke: chartTheme.axisLineStroke }}
                            tickLine={{ stroke: chartTheme.axisLineStroke }}
                            label={{
                              value: "Progress",
                              angle: -90,
                              position: "inside",
                              style: { textAnchor: "middle" },
                            }}
                          />
                          <Tooltip
                            contentStyle={tooltipContentStyle}
                            labelStyle={tooltipLabelStyle}
                            itemStyle={tooltipItemStyle}
                            formatter={percentTooltipFormatter}
                          />
                          <Legend
                            wrapperStyle={{ color: chartTheme.legendText }}
                            formatter={legendFormatter}
                          />
                          <ReferenceLine
                            y={5}
                            stroke="#66EF64"
                            strokeDasharray="6 4"
                            strokeOpacity={0.7}
                          />
                          <ReferenceLine
                            y={4}
                            stroke="#f59e0b"
                            strokeDasharray="6 4"
                            label="Readiness Threshold"
                          />
                          <Bar
                            dataKey="progress"
                            name="Progress"
                            fill={chartTheme.progressFill}
                            radius={[6, 6, 0, 0]}
                          />
                        </BarChart>
                      </ResponsiveContainer>
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} md={6}>
              <Card sx={cardStyles}>
                <CardContent sx={cardContentStyles}>
                  <Typography
                    variant="subtitle1"
                    sx={{ mb: 1, textAlign: "center" }}
                  >
                    C4 Progress Chart
                  </Typography>
                  <Box
                    sx={{
                      width: "100%",
                      overflowX: derivedC4MinWidth ? "auto" : "hidden",
                      overflowY: "hidden",
                      ...tableContainerStyles,
                    }}
                  >
                    <Box
                      sx={{
                        minWidth: derivedC4MinWidth ?? "100%",
                        height: C4_CHART_HEIGHT,
                        pr: 2,
                        backgroundColor: chartTheme.c4Bg,
                        borderRadius: "8px",
                        p: 1,
                      }}
                    >
                      <ResponsiveContainer width="100%" height="100%">
                        <BarChart data={derivedBaselineComparisonByUser}>
                          <CartesianGrid
                            strokeDasharray="3 3"
                            stroke={chartTheme.gridOnLightBg}
                          />
                          <XAxis
                            dataKey="label"
                            tick={{
                              fontSize: 12,
                              fill: chartTheme.axisTickFill,
                            }}
                            axisLine={{ stroke: chartTheme.axisLineStroke }}
                            tickLine={{ stroke: chartTheme.axisLineStroke }}
                            interval={0}
                            angle={-25}
                            textAnchor="end"
                            height={70}
                            tickFormatter={(_, idx) =>
                              (derivedBaselineComparisonByUser[idx] as any)
                                ?._rawName ??
                              (derivedBaselineComparisonByUser[idx] as any)
                                ?.name ??
                              ""
                            }
                          />
                          <YAxis
                            domain={[0, 100]}
                            ticks={[0, 20, 40, 60, 80, 100]}
                            tick={{
                              fontSize: 12,
                              fill: chartTheme.axisTickFill,
                            }}
                            axisLine={{ stroke: chartTheme.axisLineStroke }}
                            tickLine={{ stroke: chartTheme.axisLineStroke }}
                            label={{
                              value: "Percentage (%)",
                              angle: -90,
                              position: "inside",
                              style: { textAnchor: "middle" },
                              dx: -10,
                            }}
                          />
                          <Tooltip
                            contentStyle={tooltipContentStyle}
                            labelStyle={tooltipLabelStyle}
                            itemStyle={tooltipItemStyle}
                            formatter={percentTooltipFormatter}
                          />
                          <Legend
                            wrapperStyle={{ color: chartTheme.legendText }}
                            formatter={legendFormatter}
                          />
                          <Bar
                            dataKey="baseline"
                            name="Baseline"
                            fill={chartTheme.baselineFill}
                            radius={[6, 6, 0, 0]}
                          />
                          <Bar
                            dataKey="comparison"
                            name="Comparison"
                            fill={chartTheme.comparisonFill}
                            radius={[6, 6, 0, 0]}
                          />
                        </BarChart>
                      </ResponsiveContainer>
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          </>
        ) : null}

        {/* Images fallback (only when no real/derived charts available) */}
        {showImagesFallback
          ? filteredImages.map((image, index) => (
              <Grid item xs={12} md={6} key={index}>
                <Card sx={cardStyles}>
                  <CardContent sx={cardContentStyles}>
                    <Typography variant="subtitle1" sx={{ mb: 1 }}>
                      {image.name}
                    </Typography>
                    <Box
                      component="img"
                      src={`data:image/png;base64,${image.img}`}
                      alt={image.name}
                      sx={imageStyles}
                    />
                  </CardContent>
                </Card>
              </Grid>
            ))
          : null}
      </Grid>
    </Box>
  );
};
